/**
 * AutoReadingFace · SFUI-003 Phase 7a · Rex reads aloud (Web Speech API).
 *
 * Bottom-left corner (above FederationClerk). 48px Rex avatar with
 * play/pause/speed controls. Reads visible #chamber-* sections aloud.
 * Hides gracefully if speechSynthesis unavailable.
 */
import React, { useEffect, useRef, useState } from "react";

import { REX_FACE_URL, REX_ALT } from "@/lib/personaFaces";

const REX_IMG = REX_FACE_URL;

const SPEEDS = [0.75, 1, 1.25, 1.5];

export default function AutoReadingFace() {
  const [available, setAvailable] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const utterRef = useRef(null);

  useEffect(() => {
    setAvailable(typeof window !== "undefined" && "speechSynthesis" in window);
    return () => {
      try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    };
  }, []);

  if (!available) return null;
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/embed")) return null;

  const collectText = () => {
    // Pull visible text from any chamber section in viewport
    const sections = Array.from(document.querySelectorAll("[id^='chamber-'], [data-testid='big-rex-hero']"));
    const inView = sections.filter((el) => {
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight && r.bottom > 0;
    });
    const target = inView[0] || document.querySelector("[data-testid='big-rex-hero']");
    if (!target) return "";
    // Strip excessive whitespace and limit to 1500 chars
    return (target.innerText || "").replace(/\s+/g, " ").trim().slice(0, 1500);
  };

  const play = () => {
    const text = collectText();
    if (!text) return;
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    const u = new SpeechSynthesisUtterance(text);
    u.rate = speed;
    u.pitch = 0.95;
    // Male voice (Roger AI · The Essence)
    try {
      const voices = window.speechSynthesis.getVoices?.() || [];
      const en = voices.filter(v => /^en[-_]/i.test(v.lang));
      let pick = null;
      for (const hint of ["male", "daniel", "alex", "david", "mark", "george", "arthur", "aaron", "fred"]) {
        pick = en.find(v => v.name.toLowerCase().includes(hint) && !v.name.toLowerCase().includes("female"));
        if (pick) break;
      }
      if (pick) u.voice = pick;
    } catch { /* noop */ }
    u.onend = () => setPlaying(false);
    u.onerror = () => setPlaying(false);
    utterRef.current = u;
    window.speechSynthesis.speak(u);
    setPlaying(true);
  };
  const pause = () => {
    try {
      if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
        window.speechSynthesis.pause();
      } else if (window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
      } else {
        window.speechSynthesis.cancel();
      }
    } catch { /* ignore */ }
    setPlaying(false);
  };

  return (
    <div
      data-testid="auto-reading-face"
      className="fixed z-[57] flex items-center gap-2 transition-all"
      style={{
        bottom: "78px",
        left: "calc(14px + 360px + 12px)", // sits to the right of the FederationClerk on wide screens
        maxWidth: "calc(100vw - 28px)",
      }}
    >
      <button
        onClick={() => setExpanded(e => !e)}
        data-testid="auto-reading-toggle"
        aria-label={expanded ? "Hide reader" : "Show reader"}
        className="rounded-full transition-transform hover:scale-105 active:scale-95"
        style={{
          width: 44, height: 44, padding: 0, overflow: "hidden",
          border: "1.5px solid var(--gold)",
          boxShadow: "0 0 14px rgba(255,181,62,0.55), 0 4px 14px rgba(0,0,0,0.5)",
          background: "rgba(7,4,28,0.85)",
        }}
      >
        <img src={REX_IMG} alt={REX_ALT} className="w-full h-full object-cover" />
      </button>

      {expanded && (
        <div
          className="flex items-center gap-1.5 rounded-full px-2 py-1.5"
          style={{
            background: "rgba(7,4,28,0.85)",
            border: "1px solid rgba(255,181,62,0.45)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 0 16px rgba(255,181,62,0.35)",
          }}
        >
          <button
            onClick={playing ? pause : play}
            data-testid="auto-reading-play"
            aria-label={playing ? "Pause reading" : "Play reading"}
            className="font-mono text-[14px] px-2.5 py-1 rounded hover:bg-[rgba(255,181,62,0.15)]"
            style={{ color: "var(--gold)", textShadow: "0 0 6px rgba(255,181,62,0.65)" }}
          >
            {playing ? "⏸" : "▶"}
          </button>
          <select
            value={speed}
            onChange={(e) => setSpeed(Number(e.target.value))}
            data-testid="auto-reading-speed"
            aria-label="Reading speed"
            className="font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded"
            style={{
              background: "rgba(13,13,30,0.8)",
              border: "1px solid rgba(255,181,62,0.32)",
              color: "var(--text)",
            }}
          >
            {SPEEDS.map(s => <option key={s} value={s}>{s}×</option>)}
          </select>
        </div>
      )}
    </div>
  );
}
